/**
 * Sales-channel theme constants — single source of truth for every
 * place in the app that renders a sales-channel pill, badge, or icon
 * (Orders list, Order Detail, Reports, Dashboard, Returns, etc.).
 *
 * When adding a new channel adapter (Meesho, WooCommerce, eBay,
 * etc.), the only file that should need editing is THIS one.
 *
 * Usage:
 *   import { getChannelMeta, ChannelPill } from '@/lib/channels';
 *
 *   // Quick pill in a list:
 *   <ChannelPill source={order.source} order={order} />
 *
 *   // Or pull metadata directly:
 *   const { label, cls, icon } = getChannelMeta(order.source);
 */
import React from 'react';

// One entry per channel. `cls` is the Tailwind class string for the
// pill background/text/border. Keep these subtle (15% opacity bg,
// brand-tinted text/border) so they sit beside Shadcn badges without
// overpowering the row.
export const CHANNELS = {
  shopify: {
    label: 'Shopify',
    domain: 'shopify.com',
    cls: 'bg-[#7AB55C]/15 text-[#7AB55C] border-[#7AB55C]/30',
    textCls: 'text-[#7AB55C]',
  },
  amazon: {
    label: 'Amazon',
    domain: 'amazon.com',
    cls: 'bg-[#FF9900]/15 text-[#FFAC31] border-[#FF9900]/30',
    textCls: 'text-[#FFAC31]',
  },
  flipkart: {
    label: 'Flipkart',
    domain: 'flipkart.com',
    cls: 'bg-[#2874F0]/15 text-[#5B95F2] border-[#2874F0]/30',
    textCls: 'text-[#5B95F2]',
  },
  noon: {
    label: 'Noon',
    domain: 'noon.com',
    cls: 'bg-[#FEEE00]/15 text-[#F3E33D] border-[#FEEE00]/40',
    textCls: 'text-[#F3E33D]',
  },
  carrefour: {
    label: 'Carrefour',
    domain: 'carrefouruae.com',
    cls: 'bg-[#004E9F]/20 text-[#6FA8EB] border-[#004E9F]/50',
    textCls: 'text-[#6FA8EB]',
  },
  danube: {
    label: 'Danube Home',
    domain: 'danubehome.com',
    cls: 'bg-[#E87200]/15 text-[#F59E4C] border-[#E87200]/40',
    textCls: 'text-[#F59E4C]',
  },
  homecentre: {
    label: 'Home Centre',
    domain: 'homecentre.com',
    cls: 'bg-[#F58220]/15 text-[#F7A45C] border-[#F58220]/40',
    textCls: 'text-[#F7A45C]',
  },
  meesho: {
    label: 'Meesho',
    domain: 'meesho.com',
    cls: 'bg-[#F43397]/15 text-[#F66BAE] border-[#F43397]/30',
    textCls: 'text-[#F66BAE]',
  },
  woocommerce: {
    label: 'WooCommerce',
    domain: 'woocommerce.com',
    cls: 'bg-[#7F54B3]/15 text-[#A887D6] border-[#7F54B3]/30',
    textCls: 'text-[#A887D6]',
  },
  manual: {
    label: 'Manual',
    cls: 'bg-wt-bg text-wt-text-muted border-wt-border',
    textCls: 'text-wt-text-muted',
  },
  pos: {
    label: 'POS',
    cls: 'bg-wt-bg text-wt-text-muted border-wt-border',
    textCls: 'text-wt-text-muted',
  },
};

const FALLBACK = {
  label: 'Other',
  cls: 'bg-wt-bg text-wt-text-muted border-wt-border',
  textCls: 'text-wt-text-muted',
};

/**
 * Detect a channel slug from an order document. Prefers the explicit
 * `source` field; falls back to legacy marketplace-id columns so
 * orders synced before the unified `source` column existed still
 * render the right pill.
 *
 * Legacy normalisation: a few historical webhook code-paths stamped
 * `source: "shopify_webhook"` instead of `"shopify"`. We treat them
 * as the same channel here so the dashboard / pill / filter all
 * collapse them — no DB migration needed for old data.
 */
export const detectChannel = (order = {}) => {
  if (!order || typeof order !== 'object') return null;
  let explicit = (order.source || '').toLowerCase().trim();
  if (explicit === 'shopify_webhook') explicit = 'shopify';
  if (explicit) return explicit;
  if (order.shopify_order_id) return 'shopify';
  if (order.amazon_order_id) return 'amazon';
  if (order.flipkart_order_id) return 'flipkart';
  if (order.meesho_order_id) return 'meesho';
  if (order.woocommerce_order_id) return 'woocommerce';
  return null;
};

/**
 * Resolve a channel slug to its theme metadata. Unknown slugs fall
 * back to a neutral "Other" pill — never returns undefined, so callers
 * don't need null guards.
 *
 * Optional `order` param lets per-store overrides through:
 *   - `order.channel_display_name` (any channel)  → e.g. "Shopify Furniture"
 *   - `order.source_country_label` (Amazon only)  → e.g. "Amazon UAE"
 * The merchant-set `channel_display_name` always wins so the brand
 * colour is preserved while the label reflects their nickname.
 */
export const getChannelMeta = (slugOrSource, order = null) => {
  let slug = (slugOrSource || '').toString().toLowerCase().trim();
  // Collapse the legacy "shopify_webhook" slug onto "shopify".
  if (slug === 'shopify_webhook') slug = 'shopify';
  if (!slug) return FALLBACK;
  const base = CHANNELS[slug];
  let resolved = base;
  if (!resolved) {
    // Capitalise so a future channel that isn't in CHANNELS yet still
    // renders as a readable label (e.g. "Ebay") rather than "ebay".
    resolved = { ...FALLBACK, label: slug.charAt(0).toUpperCase() + slug.slice(1) };
  }
  // Per-order overrides — display_name takes precedence over country label.
  if (order && order.channel_display_name) {
    return { ...resolved, label: String(order.channel_display_name) };
  }
  if (slug === 'amazon' && order && order.source_country_label) {
    return { ...resolved, label: order.source_country_label };
  }
  return resolved;
};

/**
 * Drop-in pill component. Renders nothing if no channel can be
 * detected (so a manual order with no source field stays untagged
 * unless the caller passes `showFallback`).
 */
export const ChannelPill = ({
  source,
  order = null,
  className = '',
  showFallback = false,
  ...rest
}) => {
  const slug = source || detectChannel(order);
  if (!slug && !showFallback) return null;
  const meta = getChannelMeta(slug, order);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls} ${className}`}
      data-testid={`channel-pill-${slug || 'unknown'}`}
      title={`Order from ${meta.label}`}
      {...rest}
    >
      {meta.domain && (
        <img
          src={`https://www.google.com/s2/favicons?domain=${meta.domain}&sz=32`}
          alt=""
          className="w-3.5 h-3.5 rounded-sm"
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      {meta.label}
    </span>
  );
};

export default ChannelPill;
